import React from 'react'
import { Helmet } from 'react-helmet'
import HomeHero from '../components/home/HomeHero'
import { Link } from 'react-router-dom';
import { FiCamera, FiUsers, FiBookOpen, FiArrowRight, FiCompass } from "react-icons/fi";
import { FiShield, FiAward, FiUserCheck, FiLock } from "react-icons/fi";
import HomeCTA from '../components/home/HomeCTA';
import FadeInSection from '../components/nav/FadeInSection';
import InfiniteMarquee from '../components/home/InifiteMarquee';
import AnimatedCounter from '../components/home/AnimatedCounter';

function TrustAndStats() {
    return (
        <>
            <InfiniteMarquee />

            <section className="w-full bg-base-100 border-b border-zinc-200">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
                    <div className="py-16 px-6 flex flex-col items-center justify-center text-center border-r border-zinc-200 hover:bg-[#FFC000]/5 hover:border-[#FFC000]/30 transition-colors">
                        <AnimatedCounter end={85} suffix="%" />
                        <span className="text-xs font-bold uppercase tracking-widest text-zinc-600">Guard Retention</span>
                    </div>
                    <div className="py-16 px-6 flex flex-col items-center justify-center text-center md:border-r border-zinc-200 hover:bg-[#FFC000]/5 hover:border-[#FFC000]/30 transition-colors">
                        <AnimatedCounter end={10} suffix=" Min" />
                        <span className="text-xs font-bold uppercase tracking-widest text-zinc-600">Response</span>
                    </div>
                    <div className="py-16 px-6 flex flex-col items-center justify-center text-center border-r border-zinc-200 border-t md:border-t-0 hover:bg-[#FFC000]/5 hover:border-[#FFC000]/30 transition-colors">
                        <AnimatedCounter end={70} />
                        <span className="text-xs font-bold uppercase tracking-widest text-zinc-600">NPS Score</span>
                    </div>
                    <div className="py-16 px-6 flex flex-col items-center justify-center text-center border-t border-zinc-200 md:border-t-0 hover:bg-[#FFC000]/5 hover:border-[#FFC000]/30 transition-colors">
                        <AnimatedCounter end={5000} suffix="+" />
                        <span className="text-xs font-bold uppercase tracking-widest text-zinc-600">Training Hours</span>
                    </div>
                </div>
            </section>
        </>
    );
}

function ServicesPreview() {
    const services = [
        {
            icon: <FiShield className="w-10 h-10" />,
            title: "Manning Services",
            description: "Armed and unarmed guards, 24/7 patrols, and reliable rosters. Meticulously vetted, CID-certified, and trained beyond PSRA standards.",
            link: "/services"
        },
        {
            icon: <FiCamera className="w-10 h-10" />,
            title: "Security Systems",
            description: "Smart CCTV, alarm backup, and visitor management. Seamless tech integration for invisible, always-on protection.",
            link: "/services"
        },
        {
            icon: <FiUsers className="w-10 h-10" />,
            title: "Close Protection",
            description: "Executive bodyguards, convoy escort, and VIP screening. Discreet, professional protection for high-profile individuals.",
            link: "/services"
        },
        {
            icon: <FiBookOpen className="w-10 h-10" />,
            title: "Training & Consulting",
            description: "Guard training, threat assessments, and security audits. In-house expertise to strengthen your entire security posture.",
            link: "/services"
        }
    ];

    return (
        <section className="w-full py-20 bg-base-100 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 md:px-12">

                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-widest text-primary mb-4 block">
                            Core Competencies
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-base-content">
                            Precision Managed Services
                        </h2>
                    </div>
                    <p className="text-sm text-zinc-600 max-w-sm leading-relaxed">
                        Specialized security solutions designed to mitigate risks through a combination of human intelligence and advanced technology.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200">
                    {services.map((service, index) => (
                        <div
                            key={index}
                            className="bg-base-200 p-8 flex flex-col group border-b lg:border-b-0 lg:border-r border-zinc-200 last:border-r-0 hover:border-primary/30  transition-colors  duration-300 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                            <div className="mb-8 text-primary relative ">
                                {service.icon}
                            </div>

                            <h3 className="text-xl font-bold text-base-content mb-4 relative ">
                                {service.title}
                            </h3>
                            <p className="text-sm text-zinc-600 mb-8 flex-grow leading-relaxed relative ">
                                {service.description}
                            </p>
                            <Link
                                to={service.link}
                                className="flex items-center gap-2 text-primary text-xs font-bold uppercase tracking-widest group-hover:gap-4 transition-all relative "
                            >
                                Learn more
                                <FiArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    ))}
                </div>
                <div className="mt-12 text-center">
                    <Link
                        to="/services"
                        className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-primary transition-colors"
                    >
                        View all services and capabilities
                        <FiArrowRight className="w-4 h-4" />
                    </Link>
                </div>

            </div>
        </section>
    );
}

function HomePage() {
    return (
        <div>
            <Helmet>
                <title>Guardhart Security Services | Premium Security Solutions Kenya</title>
                <link rel="canonical" href="https://guardhart.co.ke/" />
                <meta name="description" content="Guardhart Security Services - PSRA licensed security company in Kenya offering professional guards, CCTV surveillance, close protection, and 24/7 security solutions. ISO 18788 certified." />
                <meta name="keywords" content="security company Kenya, PSRA licensed guards, CCTV surveillance Nairobi, close protection Kenya, armed guards, Guardhart" />

                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://guardhart.co.ke/" />
                <meta property="og:title" content="Guardhart Security Services | Premium Security Solutions Kenya" />
                <meta property="og:description" content="PSRA licensed security company in Kenya. Professional guards, CCTV surveillance, close protection, and 24/7 security solutions." />

                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Guardhart Security Services | Premium Security Solutions Kenya" />
                <meta property="twitter:description" content="PSRA licensed security company in Kenya. Professional guards, CCTV surveillance, close protection." />
            </Helmet>
            <HomeHero />
            <TrustAndStats />
            <FadeInSection>
                <ServicesPreview />
            </FadeInSection>
            <FadeInSection>
                <HomeCTA />
            </FadeInSection>
        </div>
    )
}

export default HomePage